import { useCallback, useRef } from "react";

import { REORDER_ERROR_MESSAGE } from "./propertyMediaOrder";

/**
 * One in-flight reorder PUT per kind + one latest queued intent.
 * Failures restore canonical state and do not auto-retry.
 */
export function usePropertyMediaReorder({
  propertyId,
  reorderRequest,
  onCanonical,
  onFailure,
}) {
  const inflightRef = useRef({ image: false, plan: false });
  const queuedRef = useRef({ image: null, plan: null });
  const generationRef = useRef({ image: 0, plan: 0 });
  const propertyIdRef = useRef(propertyId);
  const reorderRequestRef = useRef(reorderRequest);
  const onCanonicalRef = useRef(onCanonical);
  const onFailureRef = useRef(onFailure);

  propertyIdRef.current = propertyId;
  reorderRequestRef.current = reorderRequest;
  onCanonicalRef.current = onCanonical;
  onFailureRef.current = onFailure;

  const flush = useCallback(async (kind) => {
    if (inflightRef.current[kind]) return;
    inflightRef.current[kind] = true;
    try {
      while (queuedRef.current[kind]) {
        const intent = queuedRef.current[kind];
        queuedRef.current[kind] = null;
        const startedPropertyId = propertyIdRef.current;
        try {
          const result = await reorderRequestRef.current(
            startedPropertyId,
            kind,
            intent.imageIds,
          );
          if (propertyIdRef.current !== startedPropertyId) return;
          if (intent.generation !== generationRef.current[kind]) continue;
          onCanonicalRef.current?.(kind, result);
        } catch (err) {
          queuedRef.current[kind] = null;
          if (propertyIdRef.current !== startedPropertyId) return;
          onFailureRef.current?.(kind, err || new Error(REORDER_ERROR_MESSAGE));
          return;
        }
      }
    } finally {
      inflightRef.current[kind] = false;
      if (queuedRef.current[kind]) {
        flush(kind);
      }
    }
  }, []);

  const enqueue = useCallback((kind, imageIds) => {
    const id = Number(propertyIdRef.current);
    if (!Number.isInteger(id) || id <= 0) return;
    generationRef.current[kind] += 1;
    queuedRef.current[kind] = {
      imageIds: [...imageIds],
      generation: generationRef.current[kind],
    };
    flush(kind);
  }, [flush]);

  return { enqueue };
}
